import { cn } from '../../lib/utils';
import type { ProviderLabelOption } from './GmailLabelSelector';

interface OutlookLabelSelectorProps {
  categories: ProviderLabelOption[];
  selectedProviderLabelId?: string;
  onSelect: (category: ProviderLabelOption) => void;
  disabled?: boolean;
}

/**
 * Picks one Outlook category to project an internal mail tag onto. Same
 * single-select shape as `GmailLabelSelector` (a tag has at most one
 * projection per account either way), but a separate component per the
 * mail-tags redesign decision — "categories" is the correct Outlook
 * vocabulary, not "labels", and Graph assigns categories preset colors Gmail
 * labels don't have, which is exactly the kind of future divergence this
 * split leaves room for without forcing a generic abstraction now.
 *
 * Note (issue #42 rename nuance): Outlook categories are keyed by
 * *displayName*, not a stable id (`providerLabelId` here IS the display name
 * for Outlook — see `GraphProvider.listTags()`). Selecting a category and
 * later renaming the tag are two different operations; this component only
 * does the former.
 */
export function OutlookLabelSelector({
  categories,
  selectedProviderLabelId,
  onSelect,
  disabled = false,
}: OutlookLabelSelectorProps) {
  if (categories.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">No Outlook categories found for this account.</p>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {categories.map((category) => {
        const isSelected = category.providerLabelId === selectedProviderLabelId;
        return (
          <button
            key={category.providerLabelId}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(category)}
            className={cn(
              'px-2 py-1 rounded-md text-[11px] font-medium border transition-all',
              isSelected
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted/30 border-transparent hover:border-primary/30',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {category.name}
          </button>
        );
      })}
    </div>
  );
}
